const API_BASE = process.env.REACT_APP_API_URL || 'https://api.royalrevitalizations.com';

class ApiService {
  constructor() {
    this.baseUrl = API_BASE;
  }

  getToken() { return localStorage.getItem('rr_access_token'); }
  getRefreshToken() { return localStorage.getItem('rr_refresh_token'); }
  setTokens(access, refresh) {
    localStorage.setItem('rr_access_token', access);
    localStorage.setItem('rr_refresh_token', refresh);
  }
  clearTokens() {
    localStorage.removeItem('rr_access_token');
    localStorage.removeItem('rr_refresh_token');
    localStorage.removeItem('rr_user');
  }

  async request(method, path, body = null, requiresAuth = true) {
    const headers = { 'Content-Type': 'application/json' };
    if (requiresAuth) {
      const token = this.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    const config = { method, headers };
    if (body) config.body = JSON.stringify(body);

    let res = await fetch(`${this.baseUrl}${path}`, config);

    // Auto-refresh on 401
    if (res.status === 401 && requiresAuth) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.getToken()}`;
        res = await fetch(`${this.baseUrl}${path}`, { ...config, headers });
      } else {
        this.clearTokens();
        window.location.href = '/login';
        throw new Error('Session expired');
      }
    }

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  }

  // Multipart file upload (images/videos) — returns { url, filename, mimetype, size }
  async upload(file) {
    const fd = new FormData();
    fd.append('file', file);
    const doFetch = (tok) => fetch(`${this.baseUrl}/api/uploads`, {
      method: 'POST',
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      body: fd,
    });
    let res = await doFetch(this.getToken());
    if (res.status === 401) {
      const refreshed = await this.refreshToken();
      if (refreshed) { res = await doFetch(this.getToken()); }
      else { this.clearTokens(); window.location.href = '/login'; throw new Error('Session expired'); }
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data;
  }

  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return false;
      const res = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      this.setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch { return false; }
  }

  // Auth
  async register(data) { const r = await this.request('POST', '/api/auth/register', data, false); this.setTokens(r.accessToken, r.refreshToken); localStorage.setItem('rr_user', JSON.stringify(r.user)); return r; }
  async login(email, password) { const r = await this.request('POST', '/api/auth/login', { email, password }, false); this.setTokens(r.accessToken, r.refreshToken); localStorage.setItem('rr_user', JSON.stringify(r.user)); return r; }
  async getProfile() { return this.request('GET', '/api/auth/me'); }
  async updateProfile(data) { return this.request('PUT', '/api/auth/me', data); }
  async changePassword(data) { return this.request('POST', '/api/auth/change-password', data); }

  // Locations
  async getLocations() { return this.request('GET', '/api/locations', null, false); }
  async getLocation(id) { return this.request('GET', `/api/locations/${id}`, null, false); }
  async getLocationServices(id) { return this.request('GET', `/api/locations/${id}/services`, null, false); }
  async getLocationStaff(id) { return this.request('GET', `/api/locations/${id}/staff`, null, false); }

  // Services
  async getServices(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/services${qs ? '?' + qs : ''}`, null, false); }
  async getServiceCategories() { return this.request('GET', '/api/services/categories', null, false); }
  async getService(id) { return this.request('GET', `/api/services/${id}`, null, false); }

  // Bookings
  async checkAvailability(params) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/bookings/availability?${qs}`, null, false); }
  async createBooking(data) { return this.request('POST', '/api/bookings', data); }
  async getBookings(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/bookings${qs ? '?' + qs : ''}`); }
  async getBooking(id) { return this.request('GET', `/api/bookings/${id}`); }
  async cancelBooking(id, reason) { return this.request('PUT', `/api/bookings/${id}/cancel`, { reason }); }
  async checkInBooking(id) { return this.request('PUT', `/api/bookings/${id}/check-in`); }
  async completeBooking(id, data) { return this.request('PUT', `/api/bookings/${id}/complete`, data); }

  // Staff
  async getStaff(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/staff${qs ? '?' + qs : ''}`, null, false); }
  async getStaffMember(id) { return this.request('GET', `/api/staff/${id}`); }
  async getStaffSchedule(id, date) { return this.request('GET', `/api/staff/${id}/schedule?date=${date}`); }
  async clockIn(staffId, data) { return this.request('POST', `/api/staff/${staffId}/clock-in`, data); }
  async clockOut(staffId, data) { return this.request('POST', `/api/staff/${staffId}/clock-out`, data); }
  async getCommissions(staffId, params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/staff/${staffId}/commissions${qs ? '?' + qs : ''}`); }

  // Customers
  async getCustomers(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/customers${qs ? '?' + qs : ''}`); }
  async getCustomer(id) { return this.request('GET', `/api/customers/${id}`); }
  async getCustomerHistory(id) { return this.request('GET', `/api/customers/${id}/history`); }

  // Loyalty
  async getLoyaltyBalance() { return this.request('GET', '/api/loyalty/balance'); }
  async getLoyaltyHistory() { return this.request('GET', '/api/loyalty/history'); }
  async getLoyaltyTiers() { return this.request('GET', '/api/loyalty/tiers', null, false); }
  async redeemPoints(data) { return this.request('POST', '/api/loyalty/redeem', data); }

  // Gift Cards
  async purchaseGiftCard(data) { return this.request('POST', '/api/gift-cards', data); }
  async checkGiftCardBalance(code) { return this.request('GET', `/api/gift-cards/balance/${code}`, null, false); }
  async getMyGiftCards() { return this.request('GET', '/api/gift-cards/my-cards'); }

  // Reviews
  async submitReview(data) { return this.request('POST', '/api/reviews', data); }
  async getReviews(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/reviews${qs ? '?' + qs : ''}`, null, false); }
  async submitNPS(data) { return this.request('POST', '/api/reviews/nps', data); }

  // Reports (Manager+)
  async getDashboard(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/reports/dashboard${qs ? '?' + qs : ''}`); }
  async getRevenueReport(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/reports/revenue${qs ? '?' + qs : ''}`); }
  async getStaffReport(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/reports/staff${qs ? '?' + qs : ''}`); }
  async getDailyReport(params = {}) { const qs = new URLSearchParams(params).toString(); return this.request('GET', `/api/reports/daily${qs ? '?' + qs : ''}`); }

  // Staff CRUD (Manager)
  async createStaff(d) { return this.request('POST', '/api/staff', d); }
  async updateStaff(i, d) { return this.request('PUT', `/api/staff/${i}`, d); }
  async deleteStaff(i) { return this.request('DELETE', `/api/staff/${i}`); }
  async setStaffSchedule(i, s) { return this.request('POST', `/api/staff/${i}/schedule`, { schedule: s }); }
  async rescheduleBooking(i, d) { return this.request('PUT', `/api/bookings/${i}/reschedule`, d); }
}

const api = new ApiService();
export default api;
